import { action, computed, makeObservable, observable } from "mobx"

import type { ILock } from "@/domains/lock"

export type VenammSellState =
  | "idle"
  | "quoting"
  | "quote-ready"
  | "quote-failed"
  | "checking-reset-required"
  | "reset-required"
  | "resetting"
  | "reset-failed"
  | "checking-approval"
  | "approving"
  | "waiting-for-approval-confirmation"
  | "approve-failed"
  | "selling"
  | "sell-failed"
  | "complete"

export interface VenammSellStoreDAO {
  state: VenammSellState
  lock: ILock | null
  quote: {
    offer: bigint
    value: number
    haircut: number
    rate: number
    canSell: boolean
  } | null
  quoteError: string | null
  resetError: string | null
  approveError: string | null
  sellError: string | null
  resetTxHash: string | null
  approveTxHash: string | null
  sellTxHash: string | null
}

export class VenammSellStore {
  state: VenammSellState = "idle"
  lock: ILock | null = null
  quote: {
    offer: bigint
    value: number
    haircut: number
    rate: number
    canSell: boolean
  } | null = null
  quoteError: string | null = null
  resetError: string | null = null
  approveError: string | null = null
  sellError: string | null = null
  resetTxHash: string | null = null
  approveTxHash: string | null = null
  sellTxHash: string | null = null

  constructor(data?: Partial<VenammSellStoreDAO>) {
    if (data) {
      this.state = data.state ?? "idle"
      this.lock = data.lock ?? null
      this.quote = data.quote ?? null
      this.quoteError = data.quoteError ?? null
      this.resetError = data.resetError ?? null
      this.approveError = data.approveError ?? null
      this.sellError = data.sellError ?? null
      this.resetTxHash = data.resetTxHash ?? null
      this.approveTxHash = data.approveTxHash ?? null
      this.sellTxHash = data.sellTxHash ?? null
    }

    makeObservable(this, {
      lock: observable,
      quote: observable,
      quoteError: observable,
      resetError: observable,
      approveError: observable,
      sellError: observable,
      resetTxHash: observable,
      approveTxHash: observable,
      sellTxHash: observable,
      state: observable,
      clearAllErrors: action,
      clearQuoteError: action,
      clearResetError: action,
      clearApproveError: action,
      clearSellError: action,
      hasError: computed,
      isInProgress: computed,
      reset: action,
      resetTxHashUrl: computed,
      approveTxHashUrl: computed,
      sellTxHashUrl: computed,
      setLock: action,
      setQuote: action,
      setQuoteError: action,
      setResetError: action,
      setApproveError: action,
      setSellError: action,
      setResetTxHash: action,
      setApproveTxHash: action,
      setSellTxHash: action,
      setState: action,
    })
  }

  clearAllErrors(): void {
    this.quoteError = null
    this.resetError = null
    this.approveError = null
    this.sellError = null
  }

  clearQuoteError(): void {
    this.quoteError = null
  }

  clearResetError(): void {
    this.resetError = null
  }

  clearApproveError(): void {
    this.approveError = null
  }

  clearSellError(): void {
    this.sellError = null
  }

  setLock(lock: ILock | null): void {
    this.lock = lock
  }

  setQuote(
    quote: {
      offer: bigint
      value: number
      haircut: number
      rate: number
      canSell: boolean
    } | null,
  ): void {
    this.quote = quote
  }

  setQuoteError(error: string): void {
    this.quoteError = error
  }

  setResetError(error: string): void {
    this.resetError = error
  }

  setApproveError(error: string): void {
    this.approveError = error
  }

  setSellError(error: string): void {
    this.sellError = error
  }

  setResetTxHash(txHash: string): void {
    this.resetTxHash = txHash
  }

  setApproveTxHash(txHash: string): void {
    this.approveTxHash = txHash
  }

  setSellTxHash(txHash: string): void {
    this.sellTxHash = txHash
  }

  setState(state: VenammSellState): void {
    this.state = state
  }

  reset(): void {
    this.state = "idle"
    this.quote = null
    this.quoteError = null
    this.resetError = null
    this.approveError = null
    this.sellError = null
    this.resetTxHash = null
    this.approveTxHash = null
    this.sellTxHash = null
  }

  get resetTxHashUrl(): string {
    if (!this.resetTxHash) return ""
    return `https://basescan.org/tx/${this.resetTxHash}`
  }

  get approveTxHashUrl(): string {
    if (!this.approveTxHash) return ""
    return `https://basescan.org/tx/${this.approveTxHash}`
  }

  get sellTxHashUrl(): string {
    if (!this.sellTxHash) return ""
    return `https://basescan.org/tx/${this.sellTxHash}`
  }

  get hasError(): boolean {
    return this.quoteError !== null || this.resetError !== null || this.approveError !== null || this.sellError !== null
  }

  get isInProgress(): boolean {
    return [
      "quoting",
      "checking-reset-required",
      "resetting",
      "checking-approval",
      "approving",
      "waiting-for-approval-confirmation",
      "selling",
    ].includes(this.state)
  }
}
